import { useEffect, useRef, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { ClipboardList, Camera, Loader2, MapPin } from "lucide-react";

const KINDS = [["field_interview", "FIELD INTERVIEW"], ["contact_card", "CONTACT CARD"],
  ["suspicious_person", "SUSPICIOUS PERSON"], ["suspicious_vehicle", "SUSPICIOUS VEHICLE"], ["tow_request", "TOW REQUEST"]];
const P_FIELDS = [["name", "Name (Last, First)"], ["alias", "Alias"], ["dob", "DOB"], ["address", "Address"],
  ["phone", "Phone"], ["id_type", "ID Type"], ["id_number", "ID Number"], ["description", "Physical description"], ["role", "Role"]];
const V_FIELDS = [["plate", "Plate"], ["state", "State"], ["vin", "VIN"], ["year", "Year"], ["make", "Make"],
  ["model", "Model"], ["color", "Color"], ["damage", "Damage"], ["location", "Location"], ["tow_status", "Tow status"]];
const inp = "bg-[#0d0e11] border border-[#2C2D35] px-2 py-1.5 text-[11px] font-mono text-white rounded-sm outline-none focus:border-[#22D3EE]";

export default function MdtFieldForms({ myUnit, activeCc, offline = false, enqueueItem, pendingForms = [] }) {
  const [kind, setKind] = useState("");
  const [f, setF] = useState({ person: {}, vehicle: {}, narrative: "" });
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState([]);
  const [lastId, setLastId] = useState(null);
  const fileRef = useRef(null);
  const load = () => client.get("/field-forms").then((r) => setMine(r.data.forms || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { if (pendingForms.length === 0) load(); }, [pendingForms.length]); // refresh after offline queue drains
  const showV = kind === "suspicious_vehicle" || kind === "tow_request" || kind === "field_interview";
  const showP = kind && kind !== "tow_request" && kind !== "suspicious_vehicle";
  const submit = async () => {
    if ((f.narrative || "").trim().length < 10) { toast.error("A narrative is required (min 10 characters)."); return; }
    setBusy(true);
    const loc = await new Promise((res) => navigator.geolocation
      ? navigator.geolocation.getCurrentPosition((p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }), () => res({}), { timeout: 4000 }) : res({}));
    const body = { kind, ...f, unit: myUnit, incident_cc: activeCc, ...loc };
    const label = (KINDS.find(([k]) => k === kind) || [])[1] || "Form";
    if ((offline || !navigator.onLine) && enqueueItem) { enqueueItem("field_form", body, label); setF({ person: {}, vehicle: {}, narrative: "" }); setKind(""); setBusy(false); return; }
    try {
      const r = await client.post("/field-forms", body);
      setLastId(r.data.form.id);
      toast.success(`${r.data.form.label} submitted — supervisor notified. Add photos below.`);
      setF({ person: {}, vehicle: {}, narrative: "" }); setKind(""); load();
    } catch (e) {
      if (!e.response && enqueueItem) { enqueueItem("field_form", body, label); setF({ person: {}, vehicle: {}, narrative: "" }); setKind(""); }
      else toast.error(e.response?.data?.detail || "Submit failed");
    }
    setBusy(false);
  };
  const photo = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !lastId) return;
    const fd = new FormData(); fd.append("file", file);
    try { await client.post(`/field-forms/${lastId}/photo`, fd); toast.success("Photo attached"); load(); }
    catch (er) { toast.error(er.response?.data?.detail || "Photo failed"); }
    e.target.value = "";
  };
  return (
    <div className="rounded-lg border border-[#2C2D35] bg-[#141518] p-3 mt-3" data-testid="mdt-field-forms">
      <div className="font-head text-[11px] font-bold tracking-wider text-[#22D3EE] mb-2 flex items-center gap-1.5">
        <ClipboardList size={12} /> FIELD OPS FORMS <span className="font-mono text-[8px] text-[#6B7280]">auto: date/time · employee · unit · GPS</span>
      </div>
      <div className="flex gap-1.5 flex-wrap mb-2">
        {KINDS.map(([k, label]) => (
          <button key={k} data-testid={`ff-kind-${k}`} onClick={() => setKind(kind === k ? "" : k)}
            className={`px-2.5 py-2 text-[10px] font-head font-bold rounded-sm border ${kind === k ? "bg-[#22D3EE] text-black border-[#22D3EE]" : "border-[#2C2D35] text-[#C9CDD4]"}`}>{label}</button>))}
      </div>
      {kind && (
        <div className="border border-[#2C2D35] rounded-sm p-2 mb-2">
          {showP && <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mb-1.5">
            {P_FIELDS.map(([k, ph]) => <input key={k} data-testid={`ff-p-${k}`} placeholder={ph} className={inp}
              value={f.person[k] || ""} onChange={(e) => setF({ ...f, person: { ...f.person, [k]: e.target.value } })} />)}</div>}
          {showV && <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 mb-1.5">
            {V_FIELDS.map(([k, ph]) => <input key={k} data-testid={`ff-v-${k}`} placeholder={ph} className={inp}
              value={f.vehicle[k] || ""} onChange={(e) => setF({ ...f, vehicle: { ...f.vehicle, [k]: e.target.value } })} />)}</div>}
          <textarea data-testid="ff-narrative" rows={3} placeholder="Narrative (required)" className={inp + " w-full mb-1.5"}
            value={f.narrative} onChange={(e) => setF({ ...f, narrative: e.target.value })} />
          <input data-testid="ff-witnesses" placeholder="Witnesses (optional)" className={inp + " w-full mb-1.5"}
            value={f.witnesses || ""} onChange={(e) => setF({ ...f, witnesses: e.target.value })} />
          <button data-testid="ff-submit" disabled={busy} onClick={submit}
            className="w-full py-2.5 text-[11px] font-head font-bold bg-[#22D3EE] text-black rounded-sm disabled:opacity-50">
            {busy ? <Loader2 size={12} className="inline animate-spin" /> : <MapPin size={12} className="inline mr-1" />}SUBMIT WITH GPS {activeCc ? `· LINKED TO ${activeCc}` : ""}
          </button>
        </div>)}
      {lastId && (
        <button data-testid="ff-add-photo" onClick={() => fileRef.current?.click()}
          className="w-full py-2 mb-2 text-[10px] font-head font-bold border border-[#A855F7] text-[#A855F7] rounded-sm">
          <Camera size={11} className="inline mr-1" />ADD PHOTO TO LAST SUBMITTED FORM</button>)}
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={photo} />
      {pendingForms.map((p, i) => (
        <div key={`pf-${i}`} data-testid="ff-pending-offline" className="font-mono text-[10px] text-[#FCD34D] py-0.5 border-b border-[#2C2D35]/40">
          <b>⏳ {p.label}</b> · {p.ts?.slice(11, 16)}Z · WAITING FOR SIGNAL — syncs to {p.body?.incident_cc || "your forms"} on reconnect
        </div>))}
      {offline && <div className="font-mono text-[9px] text-[#F59E0B] py-1">OFFLINE — forms you submit are saved on this MDT and sync automatically. Photos can be added once reconnected.</div>}
      {mine.slice(0, 5).map((m) => (
        <div key={m.id} className="font-mono text-[10px] text-[#9CA3AF] py-0.5 border-b border-[#2C2D35]/40">
          <b className="text-[#22D3EE]">{m.label}</b> · {m.ts?.slice(0, 16)} · {m.status}
          {m.person?.name && ` · ${m.person.name}`}{m.vehicle?.plate && ` · ${m.vehicle.plate}`}
          {m.photos?.length > 0 && ` · ${m.photos.length} photo(s)`}{m.incident_cc && ` · ${m.incident_cc}`}
        </div>))}
    </div>
  );
}
